import React, { useState, useEffect, useRef } from 'react';
import {
    Shield, Pencil, X, Search, MoreVertical, UserX, ChevronLeft, ChevronRight, Crown,
    Phone, FileText, BadgeCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showSuccess, showError } from '../components/Alerts';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import { UserManagement, RoleWithPermissions, UpdateUserRequest } from '../types';
import { PERMISSIONS } from '../constants/permissions';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

// ── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
    'bg-purple-500', 'bg-blue-500', 'bg-green-600', 'bg-amber-500',
    'bg-rose-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500',
];

const ROLE_COLOR_MAP: Record<string, string> = {
    ADMIN: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    VENTAS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    COMPRAS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    CONTABILIDAD: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

function getAvatarColor(id: number): string {
    return AVATAR_PALETTE[id % AVATAR_PALETTE.length];
}

function getInitials(name: string | null, email: string): string {
    if (name) {
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : parts[0].slice(0, 2).toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
}

function getRoleColor(roleName: string): string {
    return ROLE_COLOR_MAP[roleName] ?? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
}

function formatRoleName(name: string): string {
    if (name === 'SIN_ASIGNAR') return 'Sin asignar';
    const lower = name.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function ToggleSwitch({ checked, onChange, disabled }: {
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={onChange}
            disabled={disabled}
            className={cn(
                'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background',
                'disabled:cursor-not-allowed disabled:opacity-50',
                checked ? 'bg-green-500 dark:bg-green-600' : 'bg-border',
            )}
        >
            <span className={cn(
                'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200',
                checked ? 'translate-x-[18px]' : 'translate-x-[3px]',
            )} />
        </button>
    );
}

// ── UserPopover ────────────────────────────────────────────────────────────

function UserPopover({ user }: { user: UserManagement }) {
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const show = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 8, left: rect.left });
    };

    const hide = () => {
        hideTimer.current = setTimeout(() => setPos(null), 120);
    };

    const cancelHide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
    };

    useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onMouseEnter={show}
                onMouseLeave={hide}
                className={cn(
                    'flex items-center justify-center w-9 h-9 rounded-full text-white text-xs font-semibold flex-shrink-0',
                    'ring-2 ring-transparent hover:ring-indigo-400 transition-all duration-150 cursor-default',
                    getAvatarColor(user.id),
                    pos && 'ring-indigo-500',
                )}
            >
                {getInitials(user.name, user.email)}
            </button>

            {pos && (
                <div
                    ref={panelRef}
                    onMouseEnter={cancelHide}
                    onMouseLeave={hide}
                    style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
                    className="overflow-hidden border shadow-2xl w-80 bg-card border-border rounded-2xl"
                >
                    {/* Header — gradient strip + avatar centrado */}
                    <div className="relative h-16 bg-gradient-to-r from-indigo-500/10 via-indigo-400/5 to-transparent" />
                    <div className="px-5 pb-4 -mt-8">
                        <div className={cn(
                            'flex items-center justify-center w-14 h-14 rounded-2xl text-white text-base font-bold shadow-md ring-4 ring-card',
                            getAvatarColor(user.id),
                        )}>
                            {getInitials(user.name, user.email)}
                        </div>
                        <div className="mt-2.5">
                            <p className="text-base font-bold leading-tight text-foreground">
                                {user.name ?? <span className="text-sm italic font-normal text-muted-foreground">Sin nombre</span>}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {user.isOwner ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                        <Crown className="w-3 h-3" /> Propietario
                                    </span>
                                ) : (
                                    <span className={cn(
                                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                        getRoleColor(user.roleName),
                                    )}>
                                        {formatRoleName(user.roleName)}
                                    </span>
                                )}
                                <span className={cn(
                                    'inline-flex items-center gap-1 text-xs font-medium',
                                    user.isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground',
                                )}>
                                    <span className={cn(
                                        'w-1.5 h-1.5 rounded-full',
                                        user.isActive ? 'bg-green-500' : 'bg-muted-foreground/40',
                                    )} />
                                    {user.isActive ? 'Activo' : 'Inactivo'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Datos de contacto */}
                    <div className="mx-5 mb-4 overflow-hidden border divide-y rounded-xl border-border bg-muted/30 divide-border">
                        {/* Email */}
                        <div className="flex items-center gap-3 px-3.5 py-2.5">
                            <div className="flex items-center justify-center flex-shrink-0 bg-indigo-100 rounded-lg w-7 h-7 dark:bg-indigo-900/30">
                                <BadgeCheck className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">Email</p>
                                <p className="text-xs font-medium truncate text-foreground">{user.email}</p>
                            </div>
                        </div>

                        {/* Teléfono */}
                        <div className="flex items-center gap-3 px-3.5 py-2.5">
                            <div className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30">
                                <Phone className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">Teléfono</p>
                                {user.phoneNumber ? (
                                    <p className="text-xs font-medium text-foreground">{user.phoneNumber}</p>
                                ) : (
                                    <p className="text-xs italic text-muted-foreground/60">No cargado</p>
                                )}
                            </div>
                        </div>

                        {/* Bio */}
                        <div className="flex items-start gap-3 px-3.5 py-2.5">
                            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex-shrink-0 mt-0.5">
                                <FileText className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">Bio</p>
                                {user.bio ? (
                                    <p className="text-xs font-medium leading-relaxed break-words text-foreground">{user.bio}</p>
                                ) : (
                                    <p className="text-xs italic text-muted-foreground/60">No cargada</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

const ROWS_PER_PAGE = 8;

// ── Component ──────────────────────────────────────────────────────────────

const UsersPage: React.FC = () => {
    const { hasPermission, isOwner } = useAuth();
    const canManageSecurity = isOwner() || hasPermission(PERMISSIONS.USUARIOS_ROLES.USUARIOS_GESTIONAR);

    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<UserManagement[]>([]);
    const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [editingUser, setEditingUser] = useState<UserManagement | null>(null);
    const [togglingUserId, setTogglingUserId] = useState<number | null>(null);
    const [userEditForm, setUserEditForm] = useState<UpdateUserRequest>({ roleId: 0, isActive: true });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersData, rolesData] = await Promise.all([
                userService.getAll(),
                canManageSecurity ? roleService.getAll() : Promise.resolve([] as RoleWithPermissions[]),
            ]);
            setUsers(usersData);
            setRoles(rolesData.filter(r => r.isActive));
        } catch {
            showError('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ── Filtered + paginated ───────────────────────────────────────────────

    const filtered = users.filter(u => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (u.name ?? '').toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q) ||
            u.roleName.toLowerCase().includes(q)
        );
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    // ── Handlers ──────────────────────────────────────────────────────────

    const handleToggleActive = async (user: UserManagement) => {
        if (user.isOwner) { showError('El OWNER no puede ser modificado'); return; }
        setTogglingUserId(user.id);
        try {
            if (user.isActive) {
                await userService.deactivate(user.id);
                showSuccess(`${user.name || user.email} desactivado`);
            } else {
                await userService.update(user.id, { isActive: true });
                showSuccess(`${user.name || user.email} reactivado`);
            }
            fetchData();
        } catch (error: any) {
            showError(error?.response?.data?.message || 'Error al cambiar estado');
        } finally {
            setTogglingUserId(null);
        }
    };

    const openEditModal = (user: UserManagement) => {
        if (user.isOwner) { showError('El OWNER no puede ser modificado'); return; }
        setEditingUser(user);
        setUserEditForm({ roleId: user.roleId, isActive: user.isActive });
    };

    const handleSaveUser = async () => {
        if (!editingUser) return;
        try {
            await userService.update(editingUser.id, userEditForm);
            showSuccess('Usuario actualizado exitosamente');
            setEditingUser(null);
            fetchData();
        } catch (error: any) {
            showError(error?.response?.data?.message || 'Error al actualizar usuario');
        }
    };

    // ── Stats ──────────────────────────────────────────────────────────────

    const activeCount = users.filter(u => u.isActive).length;
    const inactiveCount = users.length - activeCount;

    if (loading) return <LoadingScreen message="Cargando usuarios" fullScreen={false} />;

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="w-full h-full p-6 space-y-6">
            {/* Page header */}
            <div className="flex items-center gap-4">
                <div className="flex items-center justify-center flex-shrink-0 bg-purple-100 w-11 h-11 rounded-xl dark:bg-purple-900/30">
                    <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Usuarios</h1>
                    <p className="text-sm text-muted-foreground">Miembros de la empresa y sus accesos al sistema.</p>
                </div>
            </div>

            {/* Card */}
            <div className="overflow-hidden border shadow-sm rounded-xl bg-card border-border">

                {/* Card header */}
                <div className="px-6 py-5 border-b border-border">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                        <div>
                            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">{users.length}</span> {users.length === 1 ? 'usuario' : 'usuarios'} en total
                                <span className="text-border">·</span>
                                <span className="font-medium text-green-600 dark:text-green-400">{activeCount} {activeCount === 1 ? 'activo' : 'activos'}</span>
                                {inactiveCount > 0 && (
                                    <>
                                        <span className="text-border">·</span>
                                        <span className="text-muted-foreground">{inactiveCount} {inactiveCount === 1 ? 'inactivo' : 'inactivos'}</span>
                                    </>
                                )}
                            </p>
                        </div>
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, email o rol…"
                                value={search}
                                onChange={handleSearch}
                                className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    {pageRows.length === 0 ? (
                        <div className="py-16 text-center">
                            <UserX className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                            <p className="mb-1 text-sm font-medium text-foreground">
                                {search ? 'Sin resultados' : 'No hay usuarios'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {search ? 'Probá con otro término.' : 'Comenzá invitando el primer usuario.'}
                            </p>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/40">
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Miembro</th>
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Rol</th>
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Estado</th>
                                    {canManageSecurity && (
                                        <th className="px-5 py-3 text-xs font-semibold tracking-wide text-center uppercase text-muted-foreground">Acciones</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {pageRows.map((user) => (
                                    <tr
                                        key={user.id}
                                        className={cn(
                                            'transition-colors hover:bg-muted/30 group',
                                            !user.isActive && 'opacity-60',
                                        )}
                                    >
                                        {/* Miembro */}
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <UserPopover user={user} />
                                                <div className="min-w-0">
                                                    <p className="font-medium leading-tight text-foreground truncate max-w-[180px]">
                                                        {user.name ?? (
                                                            <span className="italic font-normal text-muted-foreground">Sin nombre</span>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[180px]">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Rol */}
                                        <td className="px-5 py-3.5">
                                            {user.isOwner ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                                    <Crown className="w-3 h-3" />
                                                    Propietario
                                                </span>
                                            ) : (
                                                <span className={cn(
                                                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                                                    getRoleColor(user.roleName),
                                                )}>
                                                    {formatRoleName(user.roleName)}
                                                </span>
                                            )}
                                        </td>

                                        {/* Estado */}
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <ToggleSwitch
                                                    checked={user.isActive}
                                                    onChange={() => handleToggleActive(user)}
                                                    disabled={togglingUserId === user.id || user.isOwner || !canManageSecurity}
                                                />
                                                <span className={cn(
                                                    'text-xs font-medium whitespace-nowrap',
                                                    user.isActive
                                                        ? 'text-green-600 dark:text-green-400'
                                                        : 'text-muted-foreground',
                                                )}>
                                                    {togglingUserId === user.id ? '…' : user.isActive ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Acciones */}
                                        {canManageSecurity && (
                                            <td className="px-5 py-3.5 text-center">
                                                {!user.isOwner && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button
                                                                className="inline-flex items-center justify-center p-2 transition-colors rounded-lg opacity-0 text-muted-foreground hover:bg-accent hover:text-foreground group-hover:opacity-100 focus:opacity-100"
                                                                title="Más acciones"
                                                            >
                                                                <MoreVertical className="w-4 h-4" />
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-44">
                                                            <DropdownMenuItem onClick={() => openEditModal(user)} className="cursor-pointer">
                                                                <Pencil className="w-4 h-4 mr-2" />
                                                                Editar usuario
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => handleToggleActive(user)}
                                                                className="cursor-pointer"
                                                                disabled={togglingUserId === user.id}
                                                            >
                                                                <X className="w-4 h-4 mr-2" />
                                                                {user.isActive ? 'Desactivar' : 'Reactivar'}
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer / Pagination */}
                {filtered.length > 0 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                            Página <span className="font-medium text-foreground">{safePage}</span> de{' '}
                            <span className="font-medium text-foreground">{totalPages}</span>
                            {filtered.length !== users.length && (
                                <> &middot; {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</>
                            )}
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={safePage === 1}
                                className="flex items-center gap-1"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={safePage === totalPages}
                                className="flex items-center gap-1"
                            >
                                Siguiente
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Edit User Dialog ── */}
            <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
                <DialogContent className="w-full max-w-md rounded-xl bg-card border-border">
                    <DialogHeader>
                        <DialogTitle className="text-foreground">Editar Usuario</DialogTitle>
                    </DialogHeader>

                    {editingUser && (
                        <>
                            {/* User identity (read-only preview) */}
                            <div className="flex items-center gap-3 px-4 py-3 border rounded-lg bg-muted/50 border-border">
                                <div className={cn(
                                    'flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-semibold flex-shrink-0',
                                    getAvatarColor(editingUser.id),
                                )}>
                                    {getInitials(editingUser.name, editingUser.email)}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium truncate text-foreground">
                                        {editingUser.name ?? <span className="italic font-normal text-muted-foreground">Sin nombre</span>}
                                    </p>
                                    <p className="text-xs truncate text-muted-foreground">{editingUser.email}</p>
                                </div>
                            </div>

                            <div className="pt-1 space-y-4">
                                {/* Rol */}
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Rol</label>
                                    <Select
                                        value={String(userEditForm.roleId ?? editingUser.roleId)}
                                        onValueChange={(val) => setUserEditForm({ ...userEditForm, roleId: Number(val) })}
                                    >
                                        <SelectTrigger className="w-full rounded-lg">
                                            <SelectValue placeholder="Seleccioná un rol" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map((role) => (
                                                <SelectItem key={role.id} value={String(role.id)}>
                                                    {formatRoleName(role.name)}
                                                    {role.description && (
                                                        <span className="ml-1 text-muted-foreground">— {role.description}</span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Estado */}
                                <div className="flex items-center justify-between px-4 py-3 border rounded-lg border-border">
                                    <div>
                                        <p className="text-sm font-medium text-foreground">Usuario activo</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {(userEditForm.isActive ?? editingUser.isActive)
                                                ? 'Puede iniciar sesión'
                                                : 'Sin acceso al sistema'}
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        checked={userEditForm.isActive ?? editingUser.isActive}
                                        onChange={() => setUserEditForm({
                                            ...userEditForm,
                                            isActive: !(userEditForm.isActive ?? editingUser.isActive),
                                        })}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-border">
                                <Button variant="outline" onClick={() => setEditingUser(null)}>Cancelar</Button>
                                <Button onClick={handleSaveUser}>Guardar Cambios</Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
